import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import { createDateAxis, createValueAxis } from "../../Trends/TrendsUtils";
import useTrend from "../../Trends/useTrend";

// Single point in a category series, derived from each `[date, category, value]`
// tuple returned by the trend query.
interface CategoryChartDatum {
  date: string;
  value: number;
  dashLength?: number;
}

// Raw trend rows are `[date, category, value]` tuples coming from parsed JSON;
// trendData from useTrend is typed `unknown`, so we narrow to this shape locally.
type TrendRow = [string, string, number];

// trendData is keyed by category name; each value is the series' data points.
type CategoryTrendData = Record<string, CategoryChartDatum[]>;

export interface AvgAgeByCategoryProps {
  // Passed directly as `scopesid` (a scope id string) in the trend query.
  selectedScope?: string | null;
  timeScale: string;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  id?: string;
  widgetKey?: string;
}

const AvgAgeByCategory: React.FC<AvgAgeByCategoryProps> = ({
  selectedScope,
  timeScale,
  filterToExecute,
  scopeToExecute,
  id,
  widgetKey,
}) => {
  const {
    trendData: data,
    trendDataError,
    trendLoading,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      timescale: timeScale,
      selection: ["age"],
      group_by: ["category"],
      aggregate_functions: ["AVG"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope,
        filters_fields_to_exclude: ["actual_status"],
      },
    },
    skip: !scopeToExecute && !selectedScope,
  });
  const [trendData, setTrendData] = useState<CategoryTrendData | null>(null);
  const [max, setMax] = useState(0);

  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart();

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      setTrendData({});
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      const rows = data as TrendRow[];
      const dataObj: CategoryTrendData = {};

      for (let i = 0; i < rows.length; i += 1) {
        const category = rows[i][1] || "Missing Category";
        if (!dataObj.hasOwnProperty(category)) {
          dataObj[category] = [];
        }

        dataObj[category].push({
          date: rows[i][0],
          value: rows[i][2],
        });
      }

      if (Object.keys(dataObj)?.length === 0) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (Object.keys(dataObj)?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      Object.keys(dataObj).forEach((item) => {
        const itemData = dataObj[item];
        if (itemData?.length >= 2) {
          itemData[itemData?.length - 2].dashLength = 8;
        }
        // Remove categories which all values are 0
        const sum = itemData?.reduce((prev, current) => {
          return prev + current.value;
        }, 0);

        if (sum === 0) {
          delete dataObj[item];
        }
      });

      let maxNum = 0;
      rows.forEach((item) => {
        item?.forEach((value) => {
          if ((value as number) > maxNum) {
            maxNum = value as number;
          }
        });
      });
      setMax(maxNum * 1.1);
      setTrendData(dataObj);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(
        "AvgAgeByCategoryTrend",
        am4charts.XYChart
      );

      chart.cursor = new am4charts.XYCursor();

      chart.paddingTop = 45;
      chart.paddingRight = 40;

      chart.colors.list = [
        am4core.color("#607aff"),
        am4core.color("#5764C4"),
        am4core.color("#4a4f8c"),
        am4core.color("#3B3B58"),
        am4core.color("#282828"),
      ];

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Age", max);
      valueAxis.getSeriesDataItem = function (
        this: am4charts.ValueAxis,
        series,
        position
      ) {
        const key = this.axisFieldName + this.axisLetter;
        const value = this.positionToValue(position);
        const dataItem = series.dataItems.getIndex(
          series.dataItems.findClosestIndex(
            value,
            (x) => {
              return (x as unknown as Record<string, number>)[key]
                ? (x as unknown as Record<string, number>)[key]
                : (undefined as unknown as number);
            },
            "any"
          )
        );
        return dataItem;
      } as typeof valueAxis.getSeriesDataItem;

      let label = dateAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 85;
      label.textAlign = "middle";

      if (trendData) {
        for (const [key] of Object.entries(trendData)) {
          createSeries(
            key,
            key !== "Missing Category" ? key.toUpperCase() : key
          );
        }
      }

      function createSeries(s: string, name: string) {
        let series = chart.series.push(new am4charts.LineSeries());
        series.dataFields.valueY = "value";
        series.dataFields.dateX = "date";
        series.name = name;
        series.tensionX = 0.8;
        series.strokeWidth = 2;
        series.tooltipText = "{name}: {value}";
        series.tooltip!.autoTextColor = false;
        // Preserve runtime behavior: original assigned the string "white";
        // amCharts coerces it at runtime, but the typed setter expects a Color.
        series.tooltip!.label.fill = "white" as unknown as am4core.Color;
        series.strokeWidth = 2;
        series.propertyFields.strokeDasharray = "dashLength";

        const bullet = series.bullets.push(new am4charts.CircleBullet());
        bullet.circle.strokeWidth = 2;
        bullet.circle.radius = 3;
        bullet.circle.fill = am4core.color("#fff");

        let segment = series.segments.template;
        segment.interactionsEnabled = true;

        let hoverState = segment.states.create("hover");
        hoverState.properties.strokeWidth = 3;

        let dimmed = segment.states.create("dimmed");
        dimmed.properties.stroke = am4core.color("#dadada");

        const bulletDimmed = series.bulletsContainer.states.create("dimmed");
        bulletDimmed.properties.stroke = am4core.color("#dadada");

        segment.events.on("over", function (event) {
          processOver(
            event.target.parent!.parent!.parent as am4charts.LineSeries
          );
        });

        segment.events.on("out", function (event) {
          processOut(
            event.target.parent!.parent!.parent as am4charts.LineSeries
          );
        });

        series.data = (trendData as CategoryTrendData)[s];

        return series;
      }

      chart.legend.itemContainers.template.events.on("over", function (event) {
        processOver(event.target.dataItem!.dataContext as am4charts.LineSeries);
      });

      chart.legend.itemContainers.template.events.on("out", function (event) {
        processOut(event.target.dataItem!.dataContext as am4charts.LineSeries);
      });

      function processOver(hoveredSeries: am4charts.LineSeries) {
        hoveredSeries.toFront();

        hoveredSeries.segments.each(function (segment) {
          segment.setState("hover");
        });

        chart.series.each(function (series) {
          if (series !== hoveredSeries) {
            (series as am4charts.LineSeries).segments.each(function (segment) {
              segment.setState("dimmed");
            });
            (series as am4charts.LineSeries).bulletsContainer.setState(
              "dimmed"
            );
          }
        });
      }

      function processOut(hoveredSeries: am4charts.LineSeries) {
        chart.series.each(function (series) {
          (series as am4charts.LineSeries).segments.each(function (segment) {
            segment.setState("default");
          });
          (series as am4charts.LineSeries).bulletsContainer.setState("default");
        });
      }

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [trendData, dataExists, createChart]);
  return (
    <Chart
      chartLoading={chartLoading}
      chartName={"AvgAgeByCategoryTrend"}
      chartError={dataError}
      chartData={dataExists}
    />
  );
};

export default AvgAgeByCategory;
